import { useState } from 'react';
import {
    Box,
    Stack,
    Typography,
    Card,
    CardContent,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    Divider,
    useTheme,
    IconButton,
    Tooltip,
    CircularProgress,
} from '@mui/material';
import {
    CalendarMonth as CalendarIcon,
    Schedule as ScheduleIcon,
    Room as LocationIcon,
    Sync as ChangeRequestIcon,
    Cancel as CanceledIcon,
    Undo as UndoIcon,
} from '@mui/icons-material';
import BaseModal from '@/components/modals/BaseModal';
import CategoryIcon from '@/components/CategoryIcon';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { close } from '@/store/uiSlice';
import { useI18nContext } from '@/i18n/i18n-react';
import {
    useGetPersonalForecastQuery,
    useCreateChangeRequestMutation,
    useCancelChangeRequestMutation,
} from '@/store/apiSlice';
import { themeConstants } from '@/theme';
import { useAppDialog } from '@/components/dialogs/AppDialog';
import { getApiErrorMessage } from '@/utils/apiError';

const PersonalForecastModal = () => {
    const dispatch = useAppDispatch();
    const open = useAppSelector((state) => state.ui.open.personalForecast);
    const onClose = () => dispatch(close('personalForecast'));
    const { LL } = useI18nContext();
    const theme = useTheme();
    const { alert } = useAppDialog();

    const [entriesCount, setEntriesCount] = useState(10);
    const [loadingOperation, setLoadingOperation] = useState<string | null>(null);

    const { data: forecast = [], isLoading } = useGetPersonalForecastQuery(entriesCount, {
        skip: !open,
    });
    const [createChangeRequest] = useCreateChangeRequestMutation();
    const [cancelChangeRequest] = useCancelChangeRequestMutation();

    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const eventDate = new Date(dateStr);
        eventDate.setHours(0, 0, 0, 0);

        const diffTime = eventDate.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        const weekdays = [
            LL.weekdays.sunday(),
            LL.weekdays.monday(),
            LL.weekdays.tuesday(),
            LL.weekdays.wednesday(),
            LL.weekdays.thursday(),
            LL.weekdays.friday(),
            LL.weekdays.saturday(),
        ];

        const formattedDate = date.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
        const weekday = weekdays[date.getDay()];

        if (diffDays === 0) {
            return `${LL.personalForecast.today()} - ${formattedDate} (${weekday})`;
        } else if (diffDays === 1) {
            return `${LL.personalForecast.tomorrow()} - ${formattedDate} (${weekday})`;
        } else if (diffDays > 0 && diffDays <= 7) {
            return `${LL.personalForecast.inDays({ count: diffDays })} - ${formattedDate} (${weekday})`;
        }

        return `${formattedDate} (${weekday})`;
    };

    const formatTime = (time?: string) => {
        if (!time || time === '00:00:00') return '';
        return time.slice(0, 5);
    };

    const handleRequestChange = async (date: string, categoryId: number, resourceId: number) => {
        const operationKey = `${date}-${categoryId}`;
        setLoadingOperation(operationKey);
        try {
            await createChangeRequest({
                date,
                category: categoryId,
                resource: resourceId,
            }).unwrap();
        } catch (error) {
            await alert(getApiErrorMessage(error));
        } finally {
            setLoadingOperation(null);
        }
    };

    const handleCancelRequest = async (date: string, categoryId: number, resourceId: number) => {
        const operationKey = `${date}-${categoryId}`;
        setLoadingOperation(operationKey);
        try {
            await cancelChangeRequest({
                date,
                category: categoryId,
                resource: resourceId,
            }).unwrap();
        } catch (error) {
            await alert(getApiErrorMessage(error));
        } finally {
            setLoadingOperation(null);
        }
    };

    return (
        <BaseModal
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            loading={isLoading}
            styledHeader={{
                icon: <CalendarIcon sx={{ fontSize: 40 }} />,
                title: LL.personalForecast.title(),
                subtitle: [LL.personalForecast.subtitle()],
                gradient: themeConstants.gradients.secondary,
            }}
        >
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
                {/* Event Count Selector */}
                <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                    <FormControl fullWidth>
                        <InputLabel>{LL.personalForecast.numberOfEntries()}</InputLabel>
                        <Select
                            value={entriesCount}
                            label={LL.personalForecast.numberOfEntries()}
                            onChange={(e) => setEntriesCount(e.target.value as number)}
                        >
                            <MenuItem value={5}>5</MenuItem>
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={15}>15</MenuItem>
                            <MenuItem value={20}>20</MenuItem>
                            <MenuItem value={30}>30</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                        </Select>
                    </FormControl>
                </Stack>

                {/* Results */}
                {forecast.length === 0 && !isLoading ? (
                    <Alert severity="info">{LL.personalForecast.noEvents()}</Alert>
                ) : (
                    <Stack spacing={2}>
                        {forecast.map((event) => (
                            <Card
                                key={event.date}
                                variant="outlined"
                                sx={{
                                    ...(event.canceled && {
                                        opacity: 0.6,
                                        borderColor: theme.palette.error.main,
                                    }),
                                }}
                            >
                                <CardContent>
                                    {/* Date Header */}
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        sx={{
                                            alignItems: 'center',
                                            mb: 1.5,
                                        }}
                                    >
                                        <CalendarIcon color="primary" />
                                        <Typography variant="h6" component="div">
                                            {formatDate(event.date)}
                                        </Typography>
                                        {event.canceled === 1 && (
                                            <Chip
                                                icon={<CanceledIcon />}
                                                label={LL.personalForecast.canceled()}
                                                color="error"
                                                size="small"
                                            />
                                        )}
                                    </Stack>

                                    {/* Event Name */}
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            fontWeight: 'bold',
                                            mb: 1,
                                        }}
                                    >
                                        {event.event || LL.personalForecast.noEventName()}
                                    </Typography>

                                    {/* Time and Location */}
                                    <Stack spacing={0.5} sx={{ mb: 2 }}>
                                        {(event.startTime || event.endTime) && (
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                sx={{
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <ScheduleIcon
                                                    sx={{ fontSize: 18, color: 'text.secondary' }}
                                                />
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: 'text.secondary',
                                                    }}
                                                >
                                                    {formatTime(event.startTime)}
                                                    {event.startTime &&
                                                        event.endTime &&
                                                        ` - ${formatTime(event.endTime)}`}
                                                </Typography>
                                            </Stack>
                                        )}
                                        {event.location && (
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                sx={{
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <LocationIcon
                                                    sx={{ fontSize: 18, color: 'text.secondary' }}
                                                />
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: 'text.secondary',
                                                    }}
                                                >
                                                    {event.location}
                                                </Typography>
                                            </Stack>
                                        )}
                                    </Stack>

                                    <Divider sx={{ my: 1.5 }} />

                                    {/* Category Assignments */}
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            color: 'text.secondary',
                                            mb: 1,
                                        }}
                                    >
                                        {LL.personalForecast.yourAssignments()}
                                    </Typography>
                                    <Stack spacing={1}>
                                        {event.assignments
                                            .filter((assignment) => assignment.isUserAssigned)
                                            .map((assignment, index) => {
                                                const operationKey = `${event.date}-${assignment.categoryId}`;
                                                const isOperationLoading =
                                                    loadingOperation === operationKey;

                                                return (
                                                    <Box
                                                        key={`${event.date}-${assignment.categoryId}-${index}`}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1,
                                                            p: 1,
                                                            borderRadius: 1,
                                                            bgcolor: 'action.selected',
                                                            position: 'relative',
                                                        }}
                                                    >
                                                        {isOperationLoading && (
                                                            <Box
                                                                sx={{
                                                                    position: 'absolute',
                                                                    top: 0,
                                                                    left: 0,
                                                                    right: 0,
                                                                    bottom: 0,
                                                                    bgcolor: 'rgba(0,0,0,0.3)',
                                                                    zIndex: 10,
                                                                    backdropFilter: 'blur(2px)',
                                                                    borderRadius: 1,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                }}
                                                            >
                                                                <CircularProgress size={20} />
                                                            </Box>
                                                        )}
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                            }}
                                                        >
                                                            <CategoryIcon
                                                                category={assignment.categoryIcon}
                                                                size="24px"
                                                            />
                                                        </Box>
                                                        <Box sx={{ flex: 1 }}>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    fontWeight: 'medium',
                                                                }}
                                                            >
                                                                {assignment.categoryName}
                                                            </Typography>
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    color: 'text.secondary',
                                                                }}
                                                            >
                                                                {LL.personalForecast.you()}
                                                            </Typography>
                                                        </Box>
                                                        {assignment.hasChangeRequest && (
                                                            <>
                                                                <Chip
                                                                    icon={<ChangeRequestIcon />}
                                                                    label={LL.personalForecast.changeRequested()}
                                                                    color="warning"
                                                                    size="small"
                                                                />
                                                                <Tooltip
                                                                    title={LL.categoryItem.cancelRequest()}
                                                                >
                                                                    <IconButton
                                                                        size="small"
                                                                        color="warning"
                                                                        onClick={() =>
                                                                            handleCancelRequest(
                                                                                event.date,
                                                                                assignment.categoryId,
                                                                                assignment.resourceId
                                                                            )
                                                                        }
                                                                        disabled={
                                                                            isOperationLoading ||
                                                                            event.canceled === 1
                                                                        }
                                                                    >
                                                                        <UndoIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </>
                                                        )}
                                                        {!assignment.hasChangeRequest &&
                                                            event.canceled !== 1 && (
                                                                <Tooltip
                                                                    title={LL.categoryItem.requestReplacement()}
                                                                >
                                                                    <IconButton
                                                                        size="small"
                                                                        color="primary"
                                                                        onClick={() =>
                                                                            handleRequestChange(
                                                                                event.date,
                                                                                assignment.categoryId,
                                                                                assignment.resourceId
                                                                            )
                                                                        }
                                                                        disabled={
                                                                            isOperationLoading
                                                                        }
                                                                    >
                                                                        <ChangeRequestIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            )}
                                                    </Box>
                                                );
                                            })}
                                    </Stack>
                                </CardContent>
                            </Card>
                        ))}
                    </Stack>
                )}
            </Box>
        </BaseModal>
    );
};

export default PersonalForecastModal;
